import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { AxiosError } from 'axios'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { RouterProvider, createRouter, redirect } from '@tanstack/react-router'
import { toast } from 'sonner'
import { handleServerError } from '@/lib/handle-server-error'
import { logger } from '@/lib/logger'
import { useAuthStore } from '@/features/auth/store/auth-store'
import { DirectionProvider } from './context/direction-provider'
import { FontProvider } from './context/font-provider'
import { ThemeProvider } from './context/theme-provider'
import { initializeTheme } from './lib/theme-initializer'
import { showErrorData } from './lib/toast'
// Generated Routes
import { routeTree } from './routeTree.gen'
// Styles
import './styles/index.css'

// Initialize theme BEFORE importing styles to prevent FOUC
initializeTheme()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (import.meta.env.DEV) logger.debug('query retry', { failureCount, error })

        if (failureCount >= 0 && import.meta.env.DEV) return false
        if (failureCount > 3 && import.meta.env.PROD) return false

        return !(
          error instanceof AxiosError &&
          [401, 403].includes(error.response?.status ?? 0)
        )
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 2 * 1000, // 10s
    },
    mutations: {
      onError: (error) => {
        handleServerError(error)

        if (error instanceof AxiosError) {
          if (error.response?.status === 304) {
            toast.error('Content not modified!')
          }
        }
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          useAuthStore.getState().auth.reset()
          redirect({
            to: '/',
          })
        }
        if (error.response?.status === 500) {
          showErrorData('服务器错误')
          // Only navigate to error page in production to avoid disrupting HMR in development
          if (import.meta.env.PROD) {
            router.navigate({ to: '/500' })
          }
        }
        if (error.response?.status === 403) {
          // router.navigate("/forbidden", { replace: true });
        }
      }
    },
  }),
})

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

//开发环境下动态加载react-scan脚本
// const loadScript = (src: string, id: string): void => {
//   // 避免重复加载
//   if (document.getElementById(id)) {
//     return
//   }

//   const script = document.createElement('script')
//   script.src = src
//   script.id = id
//   script.defer = true

//   // 将 script 标签添加到 body 或 head
//   document.head.appendChild(script)

//   script.onload = () => {
//     // eslint-disable-next-line no-console
//     console.log(`✅ 脚本 ${src} 加载完成.`)
//     // 脚本加载完成后可以执行的逻辑
//   }
//   script.onerror = () => {
//     // eslint-disable-next-line no-console
//     console.error(`❌ 脚本 ${src} 加载失败.`)
//   }
// }

// 确保只在开发环境加载
// if (import.meta.env.DEV) {
//   loadScript(
//     'https://unpkg.com/react-scan/dist/auto.global.js',
//     'react-scan-script'
//   )
// }

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <FontProvider>
            <DirectionProvider>
              <RouterProvider router={router} />
            </DirectionProvider>
          </FontProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>
  )
}
